import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { Progress as ShadcnProgress } from '@/hen-ui/shadcn/progress';
import { cn } from '@/hen-ui/utils';
import {
  ProgressFieldWrapper,
  type ProgressFieldWrapperProps,
} from './ProgressFieldWrapper';
import {
  PROGRESS_FILL_TONES,
  PROGRESS_TRACK_TONES,
  progressFillVariants,
  progressTrackVariants,
  type ProgressFillTone,
  type ProgressTrackTone,
} from './progress.variants';

export type ProgressProps = Omit<
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
  'max'
> &
  ProgressFieldWrapperProps & {
    /** Accessible name when the visible label sits outside the component; takes precedence over `label`. */
    ariaLabel?: string;
    /** Fill color. `neutral` keeps the black fill; `theme` follows the tenant theme (black fallback). */
    fillTone?: ProgressFillTone;
    /** Track color. `neutral` keeps the light-gray track; `theme`/`surface` follow tenant tokens (fallbacks apply). */
    trackTone?: ProgressTrackTone;
  };

/** Normalizes an authored prop (string, number, or text runs) to the one display string. */
function toDisplayText(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : undefined;
  }
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return value.join('');
  }
  return undefined;
}

/** Text a screen reader can use: the display text, unless it is blank. */
function toText(value: unknown): string | undefined {
  const trimmed = toDisplayText(value)?.trim();
  return trimmed ? trimmed : undefined;
}

export const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(
  (
    {
      className,
      label,
      valueLabel,
      labelPosition,
      labelSize,
      labelWeight,
      valueWeight,
      labelAlign,
      value,
      ariaLabel,
      fillTone = 'neutral',
      trackTone = 'neutral',
      'aria-label': nativeAriaLabel,
      'aria-labelledby': nativeAriaLabelledBy,
      ...props
    },
    ref,
  ) => {
    const labelId = `${React.useId()}-label`;

    const resolvedAriaLabel = toText(nativeAriaLabel) ?? toText(ariaLabel);
    const resolvedLabelledBy =
      toText(nativeAriaLabelledBy) ??
      (resolvedAriaLabel === undefined && toText(label) !== undefined
        ? labelId
        : undefined);
    const visibleValueText = toText(valueLabel);
    // cva emits no class for a value it doesn't list, which would leave the bar with no fill rule.
    const resolvedFillTone = PROGRESS_FILL_TONES.includes(fillTone)
      ? fillTone
      : 'neutral';
    const resolvedTrackTone = PROGRESS_TRACK_TONES.includes(trackTone)
      ? trackTone
      : 'neutral';

    return (
      <ProgressFieldWrapper
        label={toDisplayText(label)}
        valueLabel={toDisplayText(valueLabel)}
        labelPosition={labelPosition}
        labelSize={labelSize}
        labelWeight={labelWeight}
        valueWeight={valueWeight}
        labelAlign={labelAlign}
        labelId={labelId}
      >
        <ShadcnProgress
          ref={ref}
          className={cn(
            progressFillVariants({ fillTone: resolvedFillTone }),
            progressTrackVariants({ trackTone: resolvedTrackTone }),
            'h-2',
            className,
          )}
          value={value}
          max={100}
          aria-label={resolvedAriaLabel}
          aria-labelledby={resolvedLabelledBy}
          getValueLabel={
            visibleValueText === undefined ? undefined : () => visibleValueText
          }
          {...props}
        />
      </ProgressFieldWrapper>
    );
  },
);
Progress.displayName = 'Progress';
